import { OASFormElement } from '@oas-ui/core'

/** size 尺寸档（对齐全局三档：勾选框 14/16/18px + 字号联动） */
const VALID_SIZES = ['small', 'medium', 'large'] as const
/** status 校验态：success / warning / error（error 联动 aria-invalid） */
const VALID_STATUSES = ['error', 'warning', 'success'] as const
/** variant 形态：default 常规 / card 卡片（整块可点 + 选中描边） */
const VALID_VARIANTS = ['default', 'card'] as const

/** 枚举归一化：合法值原样返回，空/非法值静默回落默认（非法值不告警，保持输出干净） */
function normalizeChoice(raw: string, fallback: string, valid: readonly string[]): string {
  if (raw === '') return fallback
  return (valid as readonly string[]).includes(raw) ? raw : fallback
}

const STYLE = `
:host {
  display: inline-block;
  font-family: inherit;
  /* 尺寸档内部变量（data-size 镜像切换；不占公开 API，外部请用 size 属性） */
  --_box: 16px;
  --_font: var(--oas-font-size-md);
}
:host([hidden]) {
  display: none;
}
:host([data-size='small']) {
  --_box: 14px;
  --_font: var(--oas-font-size-sm);
}
:host([data-size='large']) {
  --_box: 18px;
  --_font: var(--oas-font-size-lg);
}
label {
  display: inline-flex;
  align-items: flex-start;
  gap: var(--oas-space-2);
  cursor: pointer;
  font-size: var(--_font);
  line-height: 1.5;
  color: var(--oas-color-text-primary);
}
/* 勾选框与首行文本基线对齐（行高 1.5em 与框高的差值半分） */
.box {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: var(--_box);
  height: var(--_box);
  flex-shrink: 0;
  margin-block-start: calc((1.5em - var(--_box)) / 2);
}
input {
  width: var(--_box);
  height: var(--_box);
  accent-color: var(--oas-color-primary);
  cursor: pointer;
  margin: 0;
}
input:focus-visible {
  outline: none;
  box-shadow: var(--oas-focus-ring);
}
/* 自定义指示器模式：原生 input 视觉隐藏（保留聚焦/点击语义），图标层接管显示；
   未选态给默认空心轮廓（宿主可 ::part(box) 覆写），选中态显示插槽图标 */
:host([data-custom-checked-icon]) .box input,
:host([data-custom-indeterminate-icon]) .box input {
  position: absolute;
  inset: 0;
  opacity: 0;
}
:host([data-custom-checked-icon]) .box,
:host([data-custom-indeterminate-icon]) .box {
  border: 1px solid var(--oas-color-border-strong);
  border-radius: var(--oas-radius-xs);
  box-sizing: border-box;
}
:host([data-custom-checked-icon][data-status='success']) .box,
:host([data-custom-indeterminate-icon][data-status='success']) .box {
  border-color: var(--oas-color-success);
}
:host([data-custom-checked-icon][data-status='warning']) .box,
:host([data-custom-indeterminate-icon][data-status='warning']) .box {
  border-color: var(--oas-color-warning);
}
:host([data-custom-checked-icon][data-status='error']) .box,
:host([data-custom-indeterminate-icon][data-status='error']) .box {
  border-color: var(--oas-color-danger);
}
:host([data-custom-checked-icon][disabled]) .box,
:host([data-custom-checked-icon][data-disabled]) .box,
:host([data-custom-checked-icon][data-group-disabled]) .box,
:host([data-custom-indeterminate-icon][disabled]) .box,
:host([data-custom-indeterminate-icon][data-disabled]) .box,
:host([data-custom-indeterminate-icon][data-group-disabled]) .box {
  border-color: var(--oas-color-border);
}
.indicator {
  display: none;
  position: absolute;
  inset: 0;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}
:host([data-custom-checked-icon][checked]) .indicator-checked {
  display: inline-flex;
}
:host([data-custom-indeterminate-icon][indeterminate]) .indicator-indeterminate {
  display: inline-flex;
}
/* 自定义指示器模式下焦点环上移到 .box（input opacity:0 连带自身 ring 不可见） */
:host([data-custom-checked-icon][data-focus-visible]) .box,
:host([data-custom-indeterminate-icon][data-focus-visible]) .box {
  border-radius: var(--oas-radius-xs);
  box-shadow: var(--oas-focus-ring);
}
.content {
  min-width: 0;
}
.description {
  display: block;
  font-size: var(--oas-font-size-sm);
  line-height: 1.5;
  color: var(--oas-color-text-secondary);
}
.desc-text {
  display: block;
}
.desc-text[hidden],
.description[hidden] {
  display: none;
}
/* 卡片形态：描述落在 6% 主色淡底上——text-secondary 边缘不达标，卡片内改用 secondary-strong（同 switch/radio） */
:host([data-variant='card']) .description {
  color: var(--oas-color-text-secondary-strong);
}
/* ---- label 位置：start 时文本在框的 start 侧（row-reverse 在 RTL 下自动镜像） ---- */
:host([label-position='start']) label {
  flex-direction: row-reverse;
}
/* ---- 禁用三来源（自身 / provider 注入镜像 / 组下发） ---- */
:host([disabled]) label,
:host([data-disabled]) label,
:host([data-group-disabled]) label {
  cursor: not-allowed;
  color: var(--oas-color-text-disabled);
}
:host([disabled]) input,
:host([data-disabled]) input,
:host([data-group-disabled]) input {
  cursor: not-allowed;
}
/* ---- 只读：可聚焦可复制、不切换 ---- */
:host([readonly]) label,
:host([data-group-readonly]) label {
  cursor: default;
}
/* ---- 组数量限制（max 未选项 / min 已选项）：置灰拦截 ---- */
:host([data-limit-blocked]) {
  opacity: 0.6;
}
:host([data-limit-blocked]) label {
  cursor: not-allowed;
  /* 置灰语义走禁用文字 token（同禁用三来源）：text-primary 的 0.6 淡化合成档不进禁用豁免 */
  color: var(--oas-color-text-disabled);
}
:host([data-limit-blocked]) input {
  cursor: not-allowed;
}
/* ---- status 校验态：勾选框着色（自定义指示器模式下由宿主图标自带色） ---- */
:host([data-status='success']) input {
  accent-color: var(--oas-color-success);
}
:host([data-status='warning']) input {
  accent-color: var(--oas-color-warning);
}
:host([data-status='error']) input {
  accent-color: var(--oas-color-danger);
}
/* ---- 卡片形态：整块可点 + 选中描边（选中/半选同视觉） ---- */
:host([data-variant='card']) label {
  display: flex;
  padding: var(--oas-space-3) var(--oas-space-4);
  border: 1px solid var(--oas-color-border);
  border-radius: var(--oas-radius-md);
  background: var(--oas-color-bg);
  transition: border-color var(--oas-transition-fast) var(--oas-ease-out),
    background var(--oas-transition-fast) var(--oas-ease-out);
}
:host([data-variant='card']) label:hover {
  border-color: var(--oas-color-primary);
}
:host([data-variant='card'][checked]) label {
  border-color: var(--oas-color-primary);
  background: color-mix(in srgb, var(--oas-color-primary) 6%, var(--oas-color-bg));
}
:host([data-variant='card'][indeterminate]) label {
  border-color: var(--oas-color-primary);
  background: color-mix(in srgb, var(--oas-color-primary) 6%, var(--oas-color-bg));
}
:host([data-variant='card'][disabled]) label,
:host([data-variant='card'][data-disabled]) label,
:host([data-variant='card'][data-group-disabled]) label {
  cursor: not-allowed;
  border-color: var(--oas-color-border);
  background: var(--oas-color-bg-disabled);
}
:host([data-variant='card'][data-status='success']) label {
  border-color: var(--oas-color-success);
}
:host([data-variant='card'][data-status='warning']) label {
  border-color: var(--oas-color-warning);
}
:host([data-variant='card'][data-status='error']) label,
:host([data-variant='card'][aria-invalid='true']) label {
  border-color: var(--oas-color-danger);
}
`

/** label for/input id 关联：确定性计数器（SSR 快照可重复，浏览器多实例不冲突） */
let cbIdCounter = 0

export class OASCheckbox extends OASFormElement {
  /** 原生表单集成（label for / FormData / reset / fieldset disabled）；沿静态原型链已可继承，显式声明便于阅读与检索 */
  static override formAssociated = true

  static override get observedAttributes(): string[] {
    return [
      'checked',
      'disabled',
      'indeterminate',
      'value',
      'true-value',
      'false-value',
      // required 仅驱动原生校验链（valueMissing），不透传内层 input
      'required',
      // tabindex 透传内层 input（缺省/非法不设，交还默认 Tab 序）
      'tabindex',
      'disabled-skip',
      'size',
      'status',
      'readonly',
      'variant',
      'description',
      // 组下发通道（data-group-* / data-limit-blocked 由 oas-checkbox-group 写入）
      'data-group-size',
      'data-group-disabled',
      'data-group-readonly',
      'data-group-status',
      'data-limit-blocked',
      // 表级 size 下发通道（oas-form[size] 注入）
      'data-form-size',
    ]
  }

  private input: HTMLInputElement | null = null
  private labelEl: HTMLLabelElement | null = null
  private inputId = ''
  /** 初始 checked 基线（form.reset 恢复目标）：初始渲染/受控写入跟随 checked 属性刷新 */
  private initialChecked = false
  /** 用户交互脏标记（对齐原生 dirty checkedness）：置位后基线冻结，reset 恢复基线并清脏 */
  private checkedDirty = false
  /** aria-invalid 由 status=error 设置的所有权标志（清理时只移除自己设置的，不动宿主自设值） */
  private invalidByStatus = false

  /**
   * 当前值：勾选映射（true-value/false-value）生效时为映射值（缺失侧回落布尔，对齐 switch 契约）；
   * 未设置映射时为 value 属性（选项标识/提交值通道，现状语义零破坏）。
   * 写入映射值驱动 checked（未匹配忽略）；未设置映射时布尔切换勾选、
   * 字符串写回 value 属性——property 通道必须兜住宿主框架（Vue `key in el` 探测成立后
   * 改走 property 赋值）的 `:value` 绑定，否则组内 value 静态绑定会被 setter 吞掉。
   */
  get value(): string | boolean {
    return this.mappedValue(this.hasAttr('checked'))
  }
  set value(v: string | boolean) {
    if (this.mappingActive()) {
      const tv = this.getAttr('true-value', '')
      const fv = this.getAttr('false-value', '')
      if (v === tv) this.toggleAttribute('checked', true)
      else if (v === fv) this.toggleAttribute('checked', false)
      // 未匹配映射值：忽略（不猜测语义）
      return
    }
    if (typeof v === 'boolean') this.toggleAttribute('checked', v)
    else this.setAttribute('value', String(v))
  }

  /**
   * 勾选映射是否生效：true-value/false-value 至少一项在场，且不在 checkbox-group 内。
   * 组内 value 语义固定为「选项标识」（组按 value 属性收集勾选、FormData 按选项标识提交），
   * 映射会破坏组值，故在组内一律不生效（单项独立使用才走映射，对齐 switch 契约）。
   */
  private mappingActive(): boolean {
    if (this.getAttr('true-value', '') === '' && this.getAttr('false-value', '') === '') return false
    return !this.inCheckboxGroup()
  }

  /** 是否处于 checkbox-group 内（遍历宿主链含 ShadowRoot 跳转：options 数据通道子项在组 shadow 内，closest 穿不过 ShadowRoot） */
  private inCheckboxGroup(): boolean {
    let node: Node | null = this.parentNode
    while (node) {
      if (node instanceof ShadowRoot) {
        node = node.host
        continue
      }
      if (!(node instanceof Element)) return false
      if (node.tagName === 'OAS-CHECKBOX-GROUP') return true
      node = node.parentNode
    }
    return false
  }

  /** 映射取值纯函数：映射未生效返回 value 属性（现状），生效时勾选取 true-value（缺失回落 true）、未勾取 false-value（缺失回落 false） */
  private mappedValue(next: boolean): string | boolean {
    if (!this.mappingActive()) return this.getAttr('value', '')
    const tv = this.getAttr('true-value', '')
    if (next) return tv !== '' ? tv : true
    const fv = this.getAttr('false-value', '')
    return fv !== '' ? fv : false
  }

  /** 纯函数：SSR 快照与客户端渲染共用同一份模板，保证两路径结构严格一致 */
  private template(): string {
    return `
      <style>${STYLE}</style>
      <label part="label">
        <span class="box" part="box">
          <input part="checkbox" type="checkbox" />
          <span class="indicator indicator-checked" part="indicator-checked" aria-hidden="true"><slot name="checked-icon"></slot></span>
          <span class="indicator indicator-indeterminate" part="indicator-indeterminate" aria-hidden="true"><slot name="indeterminate-icon"></slot></span>
        </span>
        <span class="content" part="content">
          <slot></slot>
          <span class="description" part="description" hidden>
            <span class="desc-text" hidden></span><slot name="description"></slot>
          </span>
        </span>
      </label>
    `
  }

  /** 缓存节点引用 + 绑定事件 + 分配确定性 id（render 与水合路径共用） */
  private bind(): void {
    this.input = this.shadow.querySelector('input')
    this.labelEl = this.shadow.querySelector('label')

    // 宿主点击 = 激活控件：外部 label 的合成 click（composedPath 起点 = 宿主）与宿主直接点击同路径，
    // 转发为内层原生 click（切换 + change 走既有处理器）；与原生「label 点击勾选」行为对齐。
    // shadow 内部点击（内层 input / 内层 label）path 起点非宿主，天然不重复。
    this.addEventListener('click', (e) => {
      if (e.defaultPrevented || this.injectDisabled() || this.isReadonly()) return
      if (e.composedPath()[0] !== this) return
      this.input?.click()
    })

    this.inputId = `oas-cb-${++cbIdCounter}`

    this.input?.addEventListener('change', () => {
      const checked = this.input!.checked
      // 用户交互置脏：冻结 reset 基线（对齐原生「用户交互不改 defaultChecked」；
      // 本组件交互会写回 checked 属性，须与「宿主受控写入刷新基线」区分）
      this.checkedDirty = true
      this.toggleAttribute('checked', checked)
      // 勾选切换是值变化点：toggleAttribute 同值时无属性变更、不触发 update，这里兜底同步
      this.syncFormValue()
      this.syncValidity()
      // value 取映射值（true-value/false-value 生效时），未设置映射保持 value 属性语义
      this.emit('change', { checked, value: this.mappedValue(checked) })
    })
    // 只读与数量限制拦截：click 阻止默认行为即可阻止勾选切换（键盘 Space 同路径）
    this.input?.addEventListener('click', (e) => {
      if (this.isReadonly()) {
        e.preventDefault()
        return
      }
      if (this.hasAttr('data-limit-blocked')) {
        e.preventDefault()
        const value = this.getAttr('value', '')
        this.emit('limit-blocked', { value })
      }
    })
    // 焦点事件直通 + focus-visible 镜像（自定义指示器模式下 ring 上移到 .box）
    this.input?.addEventListener('focus', () => {
      this.emit('focus')
      this.syncFocusVisible()
    })
    this.input?.addEventListener('blur', () => {
      this.emit('blur')
      this.removeAttribute('data-focus-visible')
    })
    // 具名插槽内容增删 → 重算指示器/描述显隐
    for (const name of ['checked-icon', 'indeterminate-icon', 'description']) {
      this.shadow.querySelector(`slot[name="${name}"]`)?.addEventListener('slotchange', () => {
        this.syncNamedSlots()
      })
    }
  }

  protected override render(): void {
    this.shadow.innerHTML = this.template()
    this.bind()
    this.update()
  }

  /** 真水合：校验 SSR 快照结构（原生 checkbox input 存在）后直接接管，跳过 shadow 重建 */
  protected override hydrate(): boolean {
    if (!this.shadow.querySelector('input[type="checkbox"]')) return false
    this.bind()
    return true
  }

  protected override update(): void {
    const input = this.input
    if (!input) return
    const checked = this.hasAttr('checked')
    // form.reset 恢复基线：初始渲染/受控写入（非用户交互的属性变化）跟随 checked 属性刷新；
    // 用户交互置脏后基线冻结
    if (!this.checkedDirty) this.initialChecked = checked
    // disabled 就近读取全局禁用注入（组件显式 disabled > 豁免 > provider 注入 > 组下发）
    const disabled = this.injectDisabled() || this.hasAttr('data-group-disabled')
    const indeterminate = this.hasAttr('indeterminate')
    const readonly = this.isReadonly()

    // 镜像最终禁用态到宿主 data-disabled（供 :host([data-disabled]) 样式消费，覆盖注入场景）
    this.toggleAttribute('data-disabled', disabled)

    // 尺寸档：单项显式 size > 组下发 > 表级下发（data-form-size）> config-provider 密度注入 > medium（链见 resolveSize）
    this.setAttribute('data-size', this.resolveSize())

    // 校验态：单项显式 status > 组下发；error 联动 aria-invalid（所有权标志防误清宿主自设值）
    const status = normalizeChoice(
      this.getAttr('status', '') || this.getAttr('data-group-status', ''),
      '',
      VALID_STATUSES,
    )
    if (status) this.setAttribute('data-status', status)
    else this.removeAttribute('data-status')
    if (status === 'error') {
      if (!this.hasAttribute('aria-invalid')) this.invalidByStatus = true
      this.setAttribute('aria-invalid', 'true')
    } else if (this.invalidByStatus) {
      this.invalidByStatus = false
      this.removeAttribute('aria-invalid')
    }

    // 形态镜像（default/card）
    this.setAttribute('data-variant', normalizeChoice(this.getAttr('variant', ''), 'default', VALID_VARIANTS))

    input.checked = checked
    input.disabled = disabled
    input.indeterminate = indeterminate
    input.setAttribute('aria-checked', indeterminate ? 'mixed' : String(checked))
    if (readonly) input.setAttribute('aria-readonly', 'true')
    else input.removeAttribute('aria-readonly')

    // tabindex 透传内层 input：合法整数值镜像，缺省/非法移除（交还默认 Tab 序）
    const tabindexRaw = this.getAttribute('tabindex')
    if (tabindexRaw != null && tabindexRaw !== '' && Number.isFinite(Number(tabindexRaw))) {
      input.tabIndex = Number(tabindexRaw)
    } else {
      input.removeAttribute('tabindex')
    }

    // 原生表单数据 + 校验链同步（form-associated；无 name 时浏览器自动不提交）。
    // checked 管提交（indeterminate 不影响 FormData，与原生一致）；无 value 属性提交缺省 'on'
    this.syncFormValue()
    this.syncValidity()

    input.id = this.inputId
    if (this.labelEl) this.labelEl.setAttribute('for', this.inputId)

    this.syncNamedSlots()
  }

  /**
   * 表单值快照（form-associated）：照原生 checkbox 语义——勾选才提交（value 属性为提交值，
   * 缺省 'on'），未勾返回 null（FormData 不含此项）；indeterminate 不影响提交。
   * 勾选映射（true-value）生效时提交映射值（对齐 switch；false-value 不进 FormData，
   * 与原生「关不提交」一致）。组内 value 语义不变（映射不生效，提交选项标识）。
   * 读 checked 属性（受控源：点击/受控写入均同步到属性），render 前也可安全取值。
   */
  protected override getFormValue(): string | null {
    if (!this.hasAttr('checked')) return null
    if (this.mappingActive()) {
      const tv = this.getAttr('true-value', '')
      if (tv !== '') return tv
    }
    return this.getAttr('value', 'on')
  }

  /** 原生校验链同步：required 且未勾 → valueMissing（flag 为 true 时 message 按 Chromium 契约必须非空） */
  private syncValidity(): void {
    if (this.hasAttr('required') && this.getFormValue() === null) {
      this.setValidity({ valueMissing: true }, this.t('form.valueMissing'))
    } else {
      this.setValidity({})
    }
  }

  /** 表单 reset：恢复 checked 属性态（受控源），不派发事件（与原生 reset 一致） */
  protected override resetFormValue(): void {
    // 恢复初始基线并清脏（与原生 reset 同步清除 dirty checkedness 一致）
    this.checkedDirty = false
    this.toggleAttribute('checked', this.initialChecked)
    if (!this.input) return
    this.input.checked = this.initialChecked
    this.syncValidity()
  }

  /** 只读判定：单项显式 readonly > 组下发（可聚焦、不切换，与 disabled 表单语义分立） */
  private isReadonly(): boolean {
    return this.hasAttr('readonly') || this.hasAttr('data-group-readonly')
  }

  /** 尺寸档解析链：单项显式 > 组下发 > 表级下发（oas-form[size]）> provider 注入 > medium（非法值静默回落 medium） */
  private resolveSize(): string {
    const own = this.getAttribute('size')
    if (own != null && own !== '') return normalizeChoice(own, 'medium', VALID_SIZES)
    const group = this.getAttribute('data-group-size')
    if (group != null && group !== '') return normalizeChoice(group, 'medium', VALID_SIZES)
    const fromForm = this.getAttribute('data-form-size')
    if (fromForm != null && fromForm !== '') return normalizeChoice(fromForm, 'medium', VALID_SIZES)
    return normalizeChoice(this.injectValue('size', 'medium'), 'medium', VALID_SIZES)
  }

  /**
   * 具名插槽同步：自定义指示器镜像 + description 显隐。
   * 约束：组件永不写 slot 的子树（含 fallback）——Chromium 中 slot 子树变更会再派发该 slot 的
   * slotchange，若由 slotchange 回调触发写入会形成微任务泵死循环（主线程饿死）；
   * 属性文本写入独立的 .desc-text 节点，与 slot 分发并列互斥显示。
   */
  private syncNamedSlots(): void {
    const hasSlotContent = (name: string): boolean => {
      const slot = this.shadow.querySelector<HTMLSlotElement>(`slot[name="${name}"]`)
      return (slot?.assignedElements().length ?? 0) > 0
    }
    this.syncIndicatorTpl('checked-icon', '.indicator-checked')
    this.syncIndicatorTpl('indeterminate-icon', '.indicator-indeterminate')
    this.toggleAttribute('data-custom-checked-icon', hasSlotContent('checked-icon'))
    this.toggleAttribute('data-custom-indeterminate-icon', hasSlotContent('indeterminate-icon'))

    const descWrap = this.shadow.querySelector<HTMLElement>('.description')
    if (!descWrap) return
    const hasDescSlot = hasSlotContent('description')
    const descText = this.getAttr('description', '')
    // 属性文本写 .desc-text（非 slot 子树）；有 slot 分发时让位隐藏
    const descTextEl = descWrap.querySelector<HTMLElement>('.desc-text')
    if (descTextEl) {
      if (descTextEl.textContent !== descText) descTextEl.textContent = descText
      descTextEl.hidden = hasDescSlot || descText === ''
    }
    descWrap.hidden = !hasDescSlot && descText === ''
  }

  /** template[slot] 指示器通道：template 分发进 slot 无视觉（内容 inert），克隆展开进指示器容器 */
  private syncIndicatorTpl(slotName: string, indicatorSelector: string): void {
    const indicator = this.shadow.querySelector<HTMLElement>(indicatorSelector)
    if (!indicator) return
    indicator.querySelector('.indicator-tpl')?.remove()
    const tpl = this.querySelector(`template[slot="${slotName}"]`)
    if (!(tpl instanceof HTMLTemplateElement)) return
    const holder = document.createElement('span')
    holder.className = 'indicator-tpl'
    holder.appendChild(tpl.content.cloneNode(true))
    indicator.appendChild(holder)
  }

  /** focus-visible 镜像（matches 查询在不支持的环境静默回落 false） */
  private syncFocusVisible(): void {
    let visible = false
    try {
      visible = this.input?.matches(':focus-visible') === true
    } catch {
      visible = false
    }
    this.toggleAttribute('data-focus-visible', visible)
  }
}
